export const COUNTRY_OPTIONS = [
  { code: 'mexico', name: 'Mexico', slug: 'mexico' },
  { code: 'canada', name: 'Canada', slug: 'canada' },
];

export const HEADING_LEVEL_OPTIONS = [
  { label: 'H2', value: 2 },
  { label: 'H3', value: 3 },
  { label: 'H4', value: 4 },
  { label: 'H5', value: 5 },
  { label: 'H6', value: 6 },
];

/** Flat CMS blocks only. */
export const COUNTRY_SECTION_CATALOG = [
  { code: 'heading', label: 'Heading (H2–H6 + rich text)' },
  { code: 'paragraph', label: 'Paragraph (rich text)' },
  { code: 'rich_text', label: 'Rich text (HTML)' },
  { code: 'image_block', label: 'Image block' },
  { code: 'border_cities_table', label: 'Dynamic table — Border cities' },
  { code: 'carousel', label: 'Carousel — States / cities' },
];

function newCarouselId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultCarouselEntryImage() {
  return {
    url: '',
    alt: '',
    title: '',
    width: '',
    height: '',
  };
}

export function normalizeCarouselEntryImage(image) {
  if (!image || typeof image !== 'object') return defaultCarouselEntryImage();
  return {
    url: image.url || '',
    alt: image.alt || '',
    title: image.title || '',
    width: image.width ?? '',
    height: image.height ?? '',
  };
}

export function hasCarouselEntryImage(image) {
  return Boolean(image && String(image.url || '').trim());
}

export function defaultCarouselEntry() {
  return {
    id: newCarouselId('entry'),
    entry_page_id: '',
    link_url: '',
    link_title: '',
    custom_title: '',
    mobile_name: '',
    custom_description: '',
    image: defaultCarouselEntryImage(),
    crossing_name: '',
    crossing_key: '',
    port_name: '',
    port_number: '',
  };
}

export function defaultCarouselCity() {
  return {
    id: newCarouselId('city'),
    badge: '',
    city_code: '',
    eyebrow: '',
    title: '',
    title_level: 3,
    description: '',
    entry_page_id: '',
    link_url: '',
    link_title: '',
    entries: [],
  };
}

export function defaultCarouselSlide() {
  return {
    id: newCarouselId('slide'),
    badge: '',
    state_code: '',
    eyebrow: '',
    title: '',
    title_level: 2,
    description: '',
    cities: [defaultCarouselCity()],
  };
}

export function defaultCarouselData() {
  return {
    slides: [defaultCarouselSlide()],
  };
}

export function clampHeadingLevel(level) {
  const n = Number(level);
  if (!Number.isFinite(n)) return 2;
  return Math.min(6, Math.max(2, Math.round(n)));
}

export function defaultImageBlockData() {
  return {
    url: '',
    alt: '',
    title: '',
    width: '',
    height: '',
    caption: '',
    credits: {
      source: { text: '', url: '' },
      author: { text: '', url: '' },
    },
    pinned: false,
    anchor: '',
  };
}

export function defaultBorderCitiesTableData() {
  return {
    title: '',
    left_header: '',
    right_header: '',
    rows: [],
    anchor: '',
  };
}

export function defaultSectionData(type) {
  switch (type) {
    case 'heading':
      return {
        level: 2,
        text: '',
        html: '',
        html_updated: '',
        anchor: '',
      };
    case 'paragraph':
      return { html: '', html_updated: '', anchor: '' };
    case 'rich_text':
      return { html: '', anchor: '' };
    case 'image_block':
      return defaultImageBlockData();
    case 'border_cities_table':
      return defaultBorderCitiesTableData();
    case 'carousel':
      return defaultCarouselData();
    default:
      return {};
  }
}

export function createSection(type) {
  return {
    id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    data: defaultSectionData(type),
  };
}

function normalizeImageCredits(credits) {
  return {
    source: {
      text: credits?.source?.text || '',
      url: credits?.source?.url || '',
    },
    author: {
      text: credits?.author?.text || '',
      url: credits?.author?.url || '',
    },
  };
}

function normalizeBorderCityRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    left: row?.left || '',
    right: row?.right || '',
  }));
}

function withHashFragment(slug, fragment) {
  const path = String(slug || '').split('#')[0].trim();
  if (!path) return '';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const hash = String(fragment || '').replace(/^#/, '').trim();
  return hash ? `${normalized}#${hash}` : normalized;
}

/** One card row per crossing name. First CBP port is kept for wait times. */
export function portsFromEntryPage(page) {
  if (!page) return [];
  const slug = page.full_slug || page.slug || '';
  const crossings = page?.crossing_names_section?.crossings_names || [];
  const rows = [];

  const pushRow = (crossingName, fragment, description, portNumber, mobileName) => {
    const name = String(crossingName || '').trim();
    const mobileTitle = String(mobileName || '').trim() || name;
    if (!name && !fragment && !mobileTitle) return;
    rows.push({
      crossing_name: name,
      crossing_key: String(fragment || '').replace(/^#/, '').trim(),
      port_name: name,
      port_number: String(portNumber || '').trim(),
      link_url: withHashFragment(slug, fragment),
      link_title: mobileTitle,
      mobile_name: mobileTitle,
      custom_title: mobileTitle,
      custom_description: '',
      source_description: String(description || '').trim(),
    });
  };

  for (const crossing of crossings) {
    const isObj = crossing && typeof crossing === 'object';
    const crossingName = isObj ? crossing.name || '' : String(crossing || '');
    const fragment = isObj ? crossing.urlFragment || crossing.key || '' : '';
    const description = isObj ? crossing.description || '' : '';
    const mobileName = isObj ? crossing.mobileName || crossing.mobile_name || '' : '';
    const ports = isObj && Array.isArray(crossing.ports) ? crossing.ports : [];
    const firstPort = ports[0];
    pushRow(crossingName, fragment, description, firstPort?.port_number, mobileName);
  }

  if (!rows.length && (page._id || slug)) {
    const fallback = page?.crossing_names_section?.name || page?.title || slug;
    pushRow(fallback, '', '', '');
  }
  return rows;
}

function normalizeCarouselEntry(entry) {
  return {
    id: entry?.id || newCarouselId('entry'),
    entry_page_id: entry?.entry_page_id ? String(entry.entry_page_id) : '',
    link_url: entry?.link_url || '',
    link_title: entry?.link_title || '',
    custom_title: entry?.mobile_name || entry?.custom_title || '',
    mobile_name: entry?.mobile_name || entry?.custom_title || '',
    custom_description: entry?.custom_description || '',
    image: normalizeCarouselEntryImage(entry?.image),
    crossing_name: entry?.crossing_name || '',
    crossing_key: entry?.crossing_key || '',
    port_name: entry?.port_name || '',
    port_number: entry?.port_number ? String(entry.port_number) : '',
  };
}

function normalizeCityEntries(city) {
  if (Array.isArray(city?.entries) && city.entries.length) {
    return city.entries.map(normalizeCarouselEntry);
  }
  if (city?.entry_page_id) {
    return [
      normalizeCarouselEntry({
        entry_page_id: city.entry_page_id,
        link_url: city.link_url,
        link_title: city.link_title,
      }),
    ];
  }
  return [];
}

function normalizeCarouselCities(cities) {
  if (!Array.isArray(cities) || !cities.length) return [defaultCarouselCity()];
  return cities.map((city) => {
    const entries = normalizeCityEntries(city);
    const first = entries[0] || {};
    return {
      id: city?.id || newCarouselId('city'),
      badge: city?.badge || '',
      city_code: city?.city_code || '',
      eyebrow: city?.eyebrow || '',
      title: city?.title || '',
      title_level: clampHeadingLevel(city?.title_level ?? 3),
      description: city?.description || '',
      entries,
      entry_page_id: first.entry_page_id || '',
      link_url: first.link_url || '',
      link_title: first.link_title || '',
    };
  });
}

function normalizeCarouselSlides(slides) {
  if (!Array.isArray(slides) || !slides.length) return [defaultCarouselSlide()];
  return slides.map((slide) => ({
    id: slide?.id || newCarouselId('slide'),
    badge: slide?.badge || '',
    state_code: slide?.state_code || '',
    eyebrow: slide?.eyebrow || '',
    title: slide?.title || '',
    title_level: clampHeadingLevel(slide?.title_level ?? 2),
    description: slide?.description || '',
    cities: normalizeCarouselCities(slide?.cities),
  }));
}

/** Normalize sections loaded from API for the editor (html_updated working copy). */
export function normalizeSectionsForEditor(sections = []) {
  return (sections || []).map((section) => {
    if (!section || typeof section !== 'object') return section;
    if (section.type === 'heading') {
      const html = section.data?.html || '';
      return {
        ...section,
        data: {
          ...defaultSectionData('heading'),
          ...(section.data || {}),
          level: clampHeadingLevel(section.data?.level),
          html,
          html_updated: html,
        },
      };
    }
    if (section.type === 'paragraph') {
      const html = section.data?.html || '';
      return {
        ...section,
        data: {
          ...defaultSectionData('paragraph'),
          ...(section.data || {}),
          html,
          html_updated: html,
        },
      };
    }
    if (section.type === 'image_block') {
      const data = section.data || {};
      const credits = data.credits
        ? normalizeImageCredits(data.credits)
        : {
            source: { text: data.credit || '', url: '' },
            author: { text: '', url: '' },
          };
      return {
        ...section,
        data: {
          ...defaultImageBlockData(),
          ...data,
          width: data.width ?? '',
          height: data.height ?? '',
          credits,
        },
      };
    }
    if (section.type === 'border_cities_table') {
      const data = section.data || {};
      return {
        ...section,
        data: {
          ...defaultBorderCitiesTableData(),
          ...data,
          rows: normalizeBorderCityRows(data.rows),
        },
      };
    }
    if (section.type === 'carousel') {
      const data = section.data || {};
      const { more_label: _ml, more_url: _mu, anchor: _an, ...rest } = data;
      return {
        ...section,
        data: {
          ...defaultCarouselData(),
          ...rest,
          slides: normalizeCarouselSlides(data.slides),
        },
      };
    }
    return section;
  });
}

/** Strip editor-only fields before persisting. */
export function serializeSectionsForSave(sections = []) {
  return (sections || []).map((section) => {
    if (!section || typeof section !== 'object') return section;
    const data = { ...(section.data || {}) };
    delete data.show_in_toc;
    delete data.state_code;
    if (section.type === 'heading') {
      data.level = clampHeadingLevel(data.level);
      data.html = data.html_updated || data.html || '';
      delete data.html_updated;
    }
    if (section.type === 'paragraph') {
      data.html = data.html_updated || data.html || '';
      delete data.html_updated;
    }
    if (section.type === 'image_block') {
      const width = Number(data.width);
      const height = Number(data.height);
      data.width = Number.isFinite(width) && width > 0 ? width : undefined;
      data.height = Number.isFinite(height) && height > 0 ? height : undefined;
      data.credits = normalizeImageCredits(data.credits);
      delete data.credit;
    }
    if (section.type === 'border_cities_table') {
      data.rows = normalizeBorderCityRows(data.rows).filter(
        (row) => row.left.trim() || row.right.trim()
      );
    }
    if (section.type === 'carousel') {
      delete data.more_label;
      delete data.more_url;
      delete data.anchor;
      data.slides = normalizeCarouselSlides(data.slides)
        .map((slide) => {
          const { more_url: _mu, ...slideRest } = slide;
          return {
            ...slideRest,
            cities: normalizeCarouselCities(slide.cities)
              .map((city) => {
                const entries = (city.entries || [])
                  .map(normalizeCarouselEntry)
                  .filter((entry) => entry.entry_page_id || entry.port_number || entry.link_url);
                const first = entries[0] || {};
                return {
                  ...city,
                  entries,
                  entry_page_id: first.entry_page_id || '',
                  link_url: first.entry_page_id ? (first.link_url || '').trim() : '',
                  link_title: first.entry_page_id ? (first.link_title || '').trim() : '',
                };
              })
              .filter(
                (city) =>
                  city.badge.trim() ||
                  city.eyebrow.trim() ||
                  city.title.trim() ||
                  city.description.trim() ||
                  city.city_code ||
                  (city.entries && city.entries.length)
              ),
          };
        })
        .filter(
          (slide) =>
            slide.badge.trim() ||
            slide.eyebrow.trim() ||
            slide.title.trim() ||
            slide.description.trim() ||
            slide.cities.length > 0
        );
      if (!data.slides.length) data.slides = [defaultCarouselSlide()];
    }
    return { ...section, data };
  });
}
